import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Divider,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { feedbackService } from '../services/feedbackService';
import { useAuth } from '../auth/AuthContext';

const TIPOS = ['Problema', 'Sugerencia', 'Pregunta'];

function FeedbackToolPanel() {
    const { user } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tipo, setTipo] = useState('Problema');
    const [titulo, setTitulo] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [enviando, setEnviando] = useState(false);

    const cargarTickets = async () => {
        try {
            setLoading(true);
            const response = await feedbackService.listarTickets();
            const datos = response?.data;
            setTickets(Array.isArray(datos) ? datos.slice(0, 8) : []);
        } catch {
            setTickets([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargarTickets();
    }, []);

    const ticketsRecientes = useMemo(() => tickets, [tickets]);

    const enviar = async () => {
        if (!titulo.trim() || !descripcion.trim()) {
            toast.error('Completá título y descripción.');
            return;
        }

        try {
            setEnviando(true);
            await feedbackService.crearTicket({
                usuarioId: user?.id || user?.idUsuario,
                titulo: `[${tipo}] ${titulo.trim()}`,
                descripcionInicial: descripcion.trim(),
                nuevasEtiquetas: [tipo],
            });
            toast.success('Feedback enviado.');
            setTitulo('');
            setDescripcion('');
            await cargarTickets();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'No se pudo enviar el feedback.');
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <Box sx={{ p: 1.5, flexShrink: 0 }}>
                <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, border: '1px solid #dce3ea', bgcolor: '#fff' }}>
                    <Stack spacing={1.25}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#17324d' }}>
                            Crear nota rápida
                        </Typography>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {TIPOS.map((item) => (
                                <Chip
                                    key={item}
                                    label={item}
                                    color={tipo === item ? 'primary' : 'default'}
                                    variant={tipo === item ? 'filled' : 'outlined'}
                                    onClick={() => setTipo(item)}
                                    sx={{ borderRadius: 999 }}
                                />
                            ))}
                        </Stack>
                        <TextField size="small" label="Título" value={titulo} onChange={(e) => setTitulo(e.target.value)} />
                        <TextField
                            size="small"
                            label="Descripción"
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                            multiline
                            minRows={3}
                        />
                        <Button
                            variant="contained"
                            onClick={enviar}
                            disabled={enviando}
                            sx={{ bgcolor: '#6f42c1', '&:hover': { bgcolor: '#5a35a3' }, textTransform: 'none', borderRadius: 999 }}
                        >
                            Guardar feedback
                        </Button>
                    </Stack>
                </Paper>
            </Box>

            <Divider />

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#17324d', mb: 1 }}>
                    Últimos tickets
                </Typography>
                {loading ? (
                    <Typography variant="body2" color="text.secondary">Cargando...</Typography>
                ) : ticketsRecientes.length === 0 ? (
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, textAlign: 'center', bgcolor: '#fff' }}>
                        <Typography variant="body2" color="text.secondary">No hay tickets recientes.</Typography>
                    </Paper>
                ) : (
                    <Stack spacing={1}>
                        {ticketsRecientes.map((ticket) => (
                            <Paper key={ticket.id} elevation={0} sx={{ p: 1.25, borderRadius: 3, border: '1px solid #dce3ea', bgcolor: '#fff' }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#17324d' }} noWrap>
                                    {ticket.titulo}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                    {ticket.estado || 'Sin estado'} · {ticket.fechaCreacion || ''}
                                </Typography>
                            </Paper>
                        ))}
                    </Stack>
                )}
            </Box>
        </Box>
    );
}

export default FeedbackToolPanel;
